import React, { Fragment } from 'react';
import { ListItemButton, Box, Typography, Avatar, Tooltip, IconButton } from '@mui/material';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LinkIcon from '@mui/icons-material/Link';
import AndroidIcon from '@mui/icons-material/Android';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RouteIcon from '@mui/icons-material/Route';
import { keyframes } from '@mui/system';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const AVATAR_SX = {
  width: 42,
  height: 42,
  bgcolor: '#757575',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const CAR_ICON_SX = {
  fontSize: 22,
  color: 'white',
};

const SERVICE_CODE_SX = {
  fontSize: '0.65rem',
  color: '#6A6A6A',
  mt: 0.5,
  fontWeight: 500,
  textAlign: 'center',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const COPY_BTN_SX = {
  p: 0.25,
  color: '#BDBDBD',
  '&:hover': { color: '#1976D2' },
};

const COPY_ICON_SX = {
  fontSize: 16,
};

const ROW_INNER_SX = {
  display: 'flex',
  width: '100%',
  gap: 1.5,
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const TEXT_BOX_SX = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const TOP_ROW_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const TOP_ACTIONS_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexShrink: 0,
};

const STATUS_ACTION_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  mt: 0.5,
};

const STATUS_WRAPPER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
};

const STATUS_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 500,
  lineHeight: 1.3,
};

const ANDROID_ICON_SX = {
  fontSize: 14,
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': { opacity: 0.8 },
};

const TIMELINE_WRAPPER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.2,
};

const TIMELINE_SEPARATOR_SX = {
  fontSize: '0.45rem',
  color: '#BDBDBD',
};

const ACTION_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 500,
  color: '#757575',
};

const ITEM_BTN_SX = {
  selected: {
    py: 1.5,
    px: 1.5,
    bgcolor: '#f0f7ff',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': { bgcolor: '#f0f7ff' },
    alignItems: 'flex-start',
    gap: 1.5,
    flexDirection: 'column',
  },
  normal: {
    py: 1.5,
    px: 1.5,
    bgcolor: 'transparent',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': { bgcolor: '#f5f5f5' },
    alignItems: 'flex-start',
    gap: 1.5,
    flexDirection: 'column',
  },
};

const NAME_SX = {
  selected: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#1976d2',
    lineHeight: 1.3,
  },
  normal: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: '#212121',
    lineHeight: 1.3,
  },
};

const INFO_ROW_SX = {
  fontSize: '0.7rem',
  color: '#616161',
  lineHeight: 1.3,
  mt: 0.25,
  fontWeight: 400,
};

const getBatteryColor = (level) => {
  if (level === undefined || level === null) return null;
  if (level > 50) return '#4CAF50';
  if (level > 20) return '#FF9800';
  return '#F44336';
};

const getAccuracyColor = (accuracy) => {
  if (accuracy === undefined || accuracy === null) return '#9E9E9E';
  if (accuracy <= 10) return '#4CAF50';
  if (accuracy <= 30) return '#FF9800';
  return '#F44336';
};

const getTimelineConfig = (currentAction) => {
  const phases = [
    { key: 'ON_WAY', icon: DirectionsCarIcon, label: 'En camino' },
    { key: 'PICKUP', icon: PersonPinCircleIcon, label: 'Recogiendo' },
    { key: 'START', icon: PlayCircleIcon, label: 'Iniciado' },
    { key: 'ACTIVE', icon: RouteIcon, label: 'En trayecto' }
  ];

  const currentIndex = phases.findIndex(phase => phase.key === currentAction);

  return phases.map((phase, index) => ({
    ...phase,
    isActive: phase.key === currentAction,
    isPast: currentIndex !== -1 && index < currentIndex,
    isFuture: currentIndex !== -1 && index > currentIndex
  }));
};

const SidebarDeviceRow = React.memo(({ index, style, items, deviceInfoById, selectedDeviceId, onDeviceSelect, onCopyLink, statusLabels, statusColors }) => {
  const device = items[index];
  if (!device) return null;

  const info = deviceInfoById.get(device.id);
  const isSelected = selectedDeviceId === device.id;

  const { batteryLevel, serviceDisplay, serviceInfo, action, versionApp, accuracy } = info ?? {};

  const batteryColor = getBatteryColor(batteryLevel);
  const statusLabel = statusLabels.get(device.id) ?? '';
  const statusColor = statusColors.get(device.status) ?? '#9E9E9E';
  const accuracyColor = getAccuracyColor(accuracy);
  const serviceCode = serviceInfo?.service_code || '';
  const isOnline = device.status === 'online';
  const timelinePhases = getTimelineConfig(action);

  const handleSelect = () => onDeviceSelect(device.id);
  const handleCopy = (e) => onCopyLink(device.id, e);

  return (
    <div style={style}>
      <ListItemButton
        sx={isSelected ? ITEM_BTN_SX.selected : ITEM_BTN_SX.normal}
        onClick={handleSelect}
      >
        <Box sx={ROW_INNER_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar sx={AVATAR_SX}>
              <DirectionsCarIcon sx={CAR_ICON_SX} />
            </Avatar>
            {serviceCode && (
              <Typography sx={SERVICE_CODE_SX}>
                {serviceCode}
              </Typography>
            )}
          </Box>
          <Box sx={TEXT_BOX_SX}>
            <Box sx={TOP_ROW_SX}>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={isSelected ? NAME_SX.selected : NAME_SX.normal}>
                  {device.name || `Dispositivo ${device.id}`}
                </Typography>

                <Typography noWrap sx={INFO_ROW_SX}>
                  {serviceDisplay ?? 'Sin servicio asignado'}
                </Typography>
              </Box>
              <Box sx={TOP_ACTIONS_SX}>
                {batteryColor && (
                  <Tooltip title={`Batería: ${batteryLevel}%`}>
                    <BatteryAlertIcon sx={{ fontSize: 14, color: batteryColor }} />
                  </Tooltip>
                )}
                {accuracy && (
                  <Tooltip title={`Precisión GPS: ${accuracy !== undefined ? Math.round(accuracy) : 'N/A'}m`}>
                    <LocationOnIcon sx={{ fontSize: 14, color: accuracyColor }} />
                  </Tooltip>
                )}
                <Tooltip title="Copiar enlace">
                  <IconButton size="small" sx={COPY_BTN_SX} onClick={handleCopy}>
                    <LinkIcon sx={COPY_ICON_SX} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={STATUS_ACTION_ROW_SX}>
              <Box sx={STATUS_WRAPPER_SX}>
                <Typography noWrap sx={{ ...STATUS_LABEL_SX, color: statusColor }}>
                  {statusLabel}
                </Typography>
                {versionApp && (
                  <Tooltip title={`Versión de la app: ${versionApp}`}>
                    <AndroidIcon sx={{ ...ANDROID_ICON_SX, color: isOnline ? '#4CAF50' : '#9E9E9E' }} />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={ACTION_LABEL_SX}>
                  Status:
                </Typography>
                <Tooltip title={action ? `Fase actual: ${timelinePhases.find(p => p.isActive)?.label || 'Desconocido'}` : 'Sin servicio activo'}>
                  <Box sx={TIMELINE_WRAPPER_SX}>
                    {timelinePhases.map((phase, idx) => {
                      const IconComponent = phase.icon;
                      return (
                        <Fragment key={phase.key}>
                          {idx > 0 && (
                            <Typography sx={TIMELINE_SEPARATOR_SX}>
                              →
                            </Typography>
                          )}
                          <IconComponent
                            sx={{
                              fontSize: 14,
                              color: phase.isActive ? '#FF5400' : '#BDBDBD',
                              animation: phase.isActive ? `${pulse} 1.5s ease-in-out infinite` : 'none',
                            }}
                          />
                        </Fragment>
                      );
                    })}
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      </ListItemButton>
    </div>
  );
},
(prevProps, nextProps) => {
  const prevItem = prevProps.items[prevProps.index];
  const nextItem = nextProps.items[nextProps.index];

  if (prevItem !== nextItem) return false;
  if (prevProps.style !== nextProps.style) return false;

  const prevSelected = prevProps.selectedDeviceId === prevItem?.id;
  const nextSelected = nextProps.selectedDeviceId === nextItem?.id;
  if (prevSelected !== nextSelected) return false;

  if (
    prevProps.statusLabels.get(prevItem?.id) !==
    nextProps.statusLabels.get(nextItem?.id)
  ) return false;

  if (
    prevProps.deviceInfoById.get(prevItem?.id) !==
    nextProps.deviceInfoById.get(nextItem?.id)
  ) return false;

  return true;
});

SidebarDeviceRow.displayName = 'SidebarDeviceRow';

export default SidebarDeviceRow;
